import type { DecisionListRequest, DecisionListType } from "@/api/client.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { cn } from "@/lib/utils.js";

const selectClass = cn(
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
);

type Props = {
  draft: DecisionListRequest;
  onDraftChange: (next: DecisionListRequest) => void;
  onApply: () => void;
};

export function DecisionFiltersBar({ draft, onDraftChange, onApply }: Props) {
  function patch(p: Partial<DecisionListRequest>) {
    onDraftChange({ ...draft, ...p });
  }

  return (
    <details className="rounded-lg border bg-card shadow-sm" data-testid="decision-filters">
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium">Filters</summary>
      <div className="grid gap-3 border-t p-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="flt-type">Type</Label>
          <select
            id="flt-type"
            className={selectClass}
            value={draft.type ?? "ban"}
            onChange={(e) => patch({ type: e.target.value as DecisionListType })}
          >
            <option value="ban">Bans</option>
            <option value="captcha">Captcha</option>
            <option value="whitelist">Whitelist</option>
            <option value="all">All types</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="flt-limit">Limit</Label>
          <select
            id="flt-limit"
            className={selectClass}
            value={String(draft.limit ?? 100)}
            onChange={(e) => patch({ limit: Number(e.target.value) })}
          >
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="250">250</option>
            <option value="500">500</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="flt-scope">Scope</Label>
          <select
            id="flt-scope"
            className={selectClass}
            value={draft.scope ?? ""}
            onChange={(e) => patch({ scope: e.target.value ? (e.target.value as DecisionListRequest["scope"]) : undefined })}
          >
            <option value="">(any)</option>
            <option value="ip">ip</option>
            <option value="range">range</option>
            <option value="country">country</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="flt-ip">IP</Label>
          <Input
            id="flt-ip"
            placeholder="203.0.113.1"
            value={draft.ip ?? ""}
            onChange={(e) => patch({ ip: e.target.value || undefined })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="flt-origins">Origins</Label>
          <Input
            id="flt-origins"
            placeholder="cscli,crowdsec"
            value={draft.origins ?? ""}
            onChange={(e) => patch({ origins: e.target.value || undefined })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="flt-scenario">Scenario</Label>
          <Input
            id="flt-scenario"
            placeholder="ssh-bf"
            value={draft.scenario ?? ""}
            onChange={(e) => patch({ scenario: e.target.value || undefined })}
          />
        </div>
        <div className="flex items-end justify-end sm:col-span-2 lg:col-span-3">
          <Button type="button" onClick={onApply}>
            Apply filters
          </Button>
        </div>
      </div>
    </details>
  );
}
